import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Enhanced GlassCard - Glass morphism card with accessibility
 * Features:
 * - WCAG 2.1 AA compliant contrast
 * - Micro-interactions on hover
 * - Flexible accent bar colors
 * - Dark mode support
 */

const GlassCard = ({ 
  title, 
  subtitle, 
  footer, 
  children, 
  className = '',
  variant = 'light',
  accentBar = true,
  accentColor = 'primary', // primary, success, warning, danger, info
  definition = null,
  source = null,
  timestamp = null,
  collapsible = false,
  defaultExpanded = true,
  priority = 'p1', // p0, p1, p2
  headerActions,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  
  const accentColors = {
    primary: 'from-urga-electricBlue to-urga-brightBlue',
    success: 'from-semantic-success to-green-400',
    warning: 'from-semantic-warning to-amber-400',
    danger: 'from-semantic-danger to-red-400',
    info: 'from-semantic-info to-blue-400',
    purple: 'from-brand-purple to-purple-400',
    bharat: 'from-brand-bharat to-orange-400',
  };

  const variantClasses = {
    light: 'bg-white/95 dark:bg-gray-900/95 border-gray-200/60 dark:border-white/10',
    dark: 'bg-gray-900/95 dark:bg-gray-800/95 border-white/10',
    ghost: 'bg-transparent border-transparent',
  };

  // Build footer
  const footerParts = [];
  if (definition) footerParts.push(`Definition: ${definition}`);
  if (source) footerParts.push(`Source: ${source}`);
  if (timestamp) footerParts.push(`Updated: ${timestamp}`);
  const finalFooter = footer || (footerParts.length > 0 ? footerParts.join(' • ') : null);

  const priorityClasses = {
    p0: 'widget-p0',
    p1: 'widget-p1',
    p2: 'widget-p2',
  };

  return (
    <article
      role="region"
      aria-label={title || 'Card'}
      aria-expanded={collapsible ? isExpanded : undefined}
      className={cn(
        'glass-card relative overflow-hidden transition-all duration-200',
        'hover:shadow-card-hover',
        variantClasses[variant],
        priorityClasses[priority],
        className
      )}
      {...props}
    >
      {/* Accent Bar */}
      {accentBar && (
        <div 
          className={cn(
            'absolute top-0 left-0 right-0 h-1 bg-gradient-to-r',
            accentColors[accentColor]
          )}
          aria-hidden="true"
        />
      )}
      
      <div className="p-6">
        {/* Header */}
        {(title || subtitle || headerActions) && (
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              {title && (
                <h3 
                  className={cn(
                    'heading-h3 text-gray-900 dark:text-white',
                    collapsible && 'cursor-pointer select-none'
                  )}
                  onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
                >
                  {collapsible && (
                    <span 
                      className={cn(
                        'inline-block mr-2 transition-transform duration-200',
                        isExpanded ? 'rotate-90' : 'rotate-0'
                      )}
                      aria-hidden="true"
                    >
                      ▶
                    </span>
                  )}
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
            {headerActions && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {headerActions}
              </div>
            )}
          </div>
        )}
        
        {/* Content */}
        <div 
          className={cn(
            'space-y-4 transition-all duration-300',
            collapsible && !isExpanded && 'h-0 overflow-hidden opacity-0'
          )}
        >
          {children}
        </div>
        
        {/* Footer */}
        {finalFooter && isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200/60 dark:border-gray-700/60">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {finalFooter}
            </p>
          </div>
        )}
      </div>
    </article>
  );
};

export default GlassCard;
